import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { apiClient } from '../../api/client'
import { useAuthStore, type User } from '../../store/authStore'
import { useFirebasePhoneAuth } from '../../hooks/useFirebasePhoneAuth'
import { formatCooldown } from '../../utils/cooldown'
import { AuthHeroLayout } from '../../components/auth/AuthHeroLayout'
import { PhoneInput } from '../../components/auth/PhoneInput'
import { PasswordInput } from '../../components/auth/PasswordInput'
import { prepareForSubmit, type CountryCode } from '@jnati/phone'
import { getErrorMessage } from '../../utils/errorMessage'

// Mirrors apps/mobile/app/(auth)/signup.tsx — 3-step wizard:
//   1. enter phone, send OTP (Firebase Phone Auth + invisible reCAPTCHA)
//   2. enter OTP code, confirm → get Firebase ID token
//   3. enter name + password, POST /api/auth/signup with firebaseToken
// Hero + card composition aligned with jnati.com marketing in #7.

function StepDots({ step }: { step: number }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {[1, 2, 3].map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              step >= s ? 'bg-gold text-navy' : 'bg-white/20 text-white/70'
            }`}
          >
            {s}
          </div>
          {i < 2 && (
            <div className={`w-6 h-0.5 ${step > s ? 'bg-gold' : 'bg-white/20'}`} />
          )}
        </div>
      ))}
    </div>
  )
}

// Auth-card focal-point button — gold per #7 (matches Login).
const goldBtn = 'w-full bg-gold text-navy rounded-lg py-3 mt-5 font-bold hover:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed transition'

export function SignupPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  // #phone-auth-unify: hook owns OTP send/verify + Firebase plumbing.
  const { state: otpState, sendOtp, verifyOtp, reset } = useFirebasePhoneAuth()

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [phone, setPhone] = useState('')
  // #intl-auth-1c: default India. Non-India is blocked in the hook with a
  // clean "coming soon" (see prepareForSubmit); India is unchanged.
  const [country, setCountry] = useState<CountryCode>('IN')
  const [otp, setOtp] = useState('')
  const [firebaseToken, setFirebaseToken] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  // #gender-required-alerts (Part 1B) — gender is now mandatory at
  // signup. Null while the user hasn't picked; the submit button
  // stays disabled until it's set.
  const [gender, setGender] = useState<'male' | 'female' | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [apiLoading, setApiLoading] = useState(false)
  const [error, setError] = useState('')

  const sending = otpState.status === 'sending'
  const verifying = otpState.status === 'verifying'
  // #otp-cooldown-ui: rate-limit countdown.
  const cooldownMs = otpState.cooldownMsRemaining ?? 0
  const isCooldown = cooldownMs > 0

  async function handleSendOtp(e: FormEvent) {
    e.preventDefault()
    setError('')
    try {
      await sendOtp(phone, { country })
      setStep(2)
    } catch (err) {
      setError(otpState.error ?? getErrorMessage(err, 'Failed to send OTP'))
    }
  }

  async function handleVerifyOtp(e: FormEvent) {
    e.preventDefault()
    if (otp.length < 6) {
      setError('Enter the 6-digit code')
      return
    }
    setError('')
    try {
      const token = await verifyOtp(otp)
      setFirebaseToken(token)
      setStep(3)
    } catch (err) {
      setError(otpState.error ?? getErrorMessage(err, 'Invalid OTP'))
    }
  }

  async function handleCompleteSignup(e: FormEvent) {
    e.preventDefault()
    if (!firstName.trim() || !lastName.trim()) {
      setError('Enter your first and last name')
      return
    }
    if (!gender) {
      setError('Please select your gender (Male/Female)')
      return
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    if (password !== confirmPwd) {
      setError('Passwords do not match')
      return
    }
    // #e164-migration Release C: send the E.164 value (India → +91<national>,
    // non-India → +CC…). OTP already succeeded for this phone/country, so prep
    // is ok; guard defensively.
    const prep = prepareForSubmit(phone, country)
    if (!prep.ok) { setError(prep.message); return }
    setError('')
    setApiLoading(true)
    try {
      const res = await apiClient.post('/api/auth/signup', {
        phoneNumber: prep.e164,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        firebaseToken,
        gender,
      })
      const data = res.data
      const user: User = {
        id: data.userId,
        firstName: data.firstName ?? firstName.trim(),
        lastName: data.lastName ?? lastName.trim(),
        email: data.email ?? '',
        profilePhoto: data.profilePhoto ?? null,
        preferredLanguage: data.preferredLanguage ?? 'en',
        gender: data.gender ?? null,
        kinshipLabelPreference: data.kinshipLabelPreference ?? 'telugu_script',
        isAdmin: data.isAdmin === true,
      }
      localStorage.setItem('token', data.token)
      // #auth-refresh-interceptor: see Login.tsx for rationale.
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken)
      localStorage.setItem('userId', String(data.userId))
      localStorage.setItem('user', JSON.stringify(user))
      localStorage.setItem('language', user.preferredLanguage)
      setAuth(user, data.token)
      navigate('/', { replace: true })
    } catch (err) {
      setError(getErrorMessage(err, 'Signup failed'))
    } finally {
      setApiLoading(false)
    }
  }

  return (
    <AuthHeroLayout
      hideTagline
      belowLogo={<StepDots step={step} />}
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="text-gold font-bold hover:underline">Login</Link>
        </>
      }
    >
      {error && <p className="text-danger text-sm mb-3" role="alert">{error}</p>}

      {step === 1 && (
        <form onSubmit={handleSendOtp}>
          <h2 className="text-navy text-xl font-extrabold mb-4">Verify your phone</h2>
          <PhoneInput value={phone} onChange={setPhone} country={country} onCountryChange={setCountry} disabled={sending || apiLoading} topless />
          {/* #copy-1.1: reinforce phone-as-private-verifier framing at
              the FIRST phone-collection moment. Same line lands on
              AddPhone (#copy-1) and EditProfile. */}
          <p className="text-gray-500 text-xs mt-2">
            Used for account verification and recovery. Not shared with other users.
          </p>
          <button type="submit" disabled={sending || apiLoading || isCooldown} className={goldBtn}>
            {sending
              ? 'Sending OTP…'
              : isCooldown
                ? `Try again in ${formatCooldown(cooldownMs)}`
                : 'Send OTP'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleVerifyOtp}>
          <h2 className="text-navy text-xl font-extrabold mb-2">Enter the 6-digit code</h2>
          <p className="text-gray-600 text-sm mb-4">Sent to +91 {phone}</p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full text-center text-2xl tracking-widest border-2 border-border rounded-lg py-3 text-navy outline-none"
          />
          <button type="submit" disabled={verifying || otp.length < 6} className={goldBtn}>
            {verifying ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={() => { setStep(1); setOtp(''); reset() }}
            className="w-full text-navy text-sm font-semibold mt-3 hover:underline"
          >
            Change phone number
          </button>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={handleCompleteSignup}>
          <h2 className="text-navy text-xl font-extrabold mb-4">Your details</h2>

          <label className="block text-navy text-xs font-semibold mb-1">First name</label>
          <input
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full border-2 border-border rounded-lg px-3 py-3 text-navy text-sm outline-none"
          />

          <label className="block text-navy text-xs font-semibold mb-1 mt-3">Last name</label>
          <input
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full border-2 border-border rounded-lg px-3 py-3 text-navy text-sm outline-none"
          />

          {/* #gender-required-alerts (Part 1B) — mandatory gender picker.
              Two chip buttons; Navy fill when active. Backed by state
              that gates the submit button below. */}
          <label className="block text-navy text-xs font-semibold mb-1 mt-3">
            Gender <span className="text-danger">*</span>
          </label>
          <div className="flex gap-2">
            {(['male', 'female'] as const).map((g) => {
              const active = gender === g
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  disabled={apiLoading}
                  className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-semibold transition ${
                    active
                      ? 'bg-navy text-white border-navy'
                      : 'bg-white text-navy border-border hover:bg-chatBg'
                  }`}
                >
                  {g === 'male' ? 'Male' : 'Female'}
                </button>
              )
            })}
          </div>
          <p className="text-gray-500 text-[11px] mt-1">
            Needed to display your kinship relations correctly.
          </p>

          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            disabled={apiLoading}
          />
          <PasswordInput
            value={confirmPwd}
            onChange={setConfirmPwd}
            label="Confirm password"
            autoComplete="new-password"
            disabled={apiLoading}
          />

          <button
            type="submit"
            disabled={apiLoading || !gender || !firstName.trim() || !lastName.trim()}
            className={goldBtn}
          >
            {apiLoading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
      )}
    </AuthHeroLayout>
  )
}
